import { useState } from 'react'
import { AlertTriangle, Copy, KeyRound, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  ALL_SCOPES,
  DEFAULT_TOKEN_SCOPES,
  useCreateToken,
  useRevokeToken,
  useTokens,
} from '../api/tokens'

export function ApiTokensPage() {
  const { t } = useTranslation('auth')
  const { data: tokens, isLoading } = useTokens()
  const create = useCreateToken()
  const revoke = useRevokeToken()

  const EXPIRY_OPTIONS: { labelKey: string; seconds: number | null }[] = [
    { labelKey: 'tokens.expiry.never', seconds: null },
    { labelKey: 'tokens.expiry.days30', seconds: 60 * 60 * 24 * 30 },
    { labelKey: 'tokens.expiry.days90', seconds: 60 * 60 * 24 * 90 },
    { labelKey: 'tokens.expiry.year1', seconds: 60 * 60 * 24 * 365 },
  ]

  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>(DEFAULT_TOKEN_SCOPES)
  const [expiry, setExpiry] = useState<number | null>(null)
  const [revealed, setRevealed] = useState<{ raw: string; name: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onCreate(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      const res = await create.mutateAsync({
        name: name.trim(),
        scopes,
        expires_in_seconds: expiry,
      })
      setRevealed({ raw: res.raw, name: res.token.name })
      setShowCreate(false)
      setName('')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  function toggleScope(key: string) {
    setScopes((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]))
  }

  async function copyRaw() {
    if (!revealed) return
    await navigator.clipboard.writeText(revealed.raw)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('tokens.title')}</h1>
          <p className="text-sm text-text-secondary">
            {t('tokens.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> {t('tokens.newToken')}
        </button>
      </header>

      {revealed && (
        <div className="space-y-2 rounded-lg border border-warning/50 bg-warning/10 p-4">
          <div className="flex items-center gap-2 font-medium text-warning">
            <AlertTriangle className="h-5 w-5" />
            {t('tokens.copyNow')}
          </div>
          <div className="text-sm text-text-secondary">{t('tokens.tokenLabel', { name: revealed.name })}</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded bg-background p-2 font-mono text-xs">
              {revealed.raw}
            </code>
            <button
              onClick={copyRaw}
              className="rounded-lg border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-elevated"
            >
              <Copy className="h-4 w-4" /> {copied ? t('tokens.copied') : t('tokens.copy')}
            </button>
          </div>
          <button
            onClick={() => setRevealed(null)}
            className="text-xs text-text-secondary underline"
          >
            {t('tokens.savedDismiss')}
          </button>
        </div>
      )}

      {showCreate && (
        <form onSubmit={onCreate} className="space-y-3 rounded-lg border border-border bg-surface p-4">
          <h2 className="font-medium">{t('tokens.createTitle')}</h2>
          <div>
            <label className="block text-xs font-medium text-text-secondary">{t('tokens.nameLabel')}</label>
            <input
              required
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('tokens.namePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">{t('tokens.scopesLabel')}</label>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ALL_SCOPES.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={scopes.includes(key)}
                    onChange={() => toggleScope(key)}
                  />
                  <span className="font-mono text-xs">{key}</span>
                  <span className="text-xs text-text-secondary">— {label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">{t('tokens.expiryLabel')}</label>
            <select
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={expiry === null ? '' : String(expiry)}
              onChange={(e) => setExpiry(e.target.value ? Number(e.target.value) : null)}
            >
              {EXPIRY_OPTIONS.map((o) => (
                <option key={o.labelKey} value={o.seconds === null ? '' : o.seconds}>
                  {t(o.labelKey)}
                </option>
              ))}
            </select>
          </div>
          {error && <div className="text-sm text-error">{error}</div>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={create.isPending || scopes.length === 0}
              className="rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {create.isPending ? t('tokens.creating') : t('tokens.createCta')}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-surface-elevated"
            >
              {t('common:actions.cancel')}
            </button>
          </div>
        </form>
      )}

      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <header className="border-b border-border px-4 py-3 font-medium">{t('tokens.activeTokens')}</header>
        {isLoading && <div className="p-4 text-sm text-text-secondary">{t('common.loading')}</div>}
        {!isLoading && (!tokens || tokens.length === 0) && (
          <div className="p-6 text-center text-sm text-text-secondary">
            {t('tokens.noTokens')}
          </div>
        )}
        {tokens && tokens.length > 0 && (
          <ul className="divide-y divide-border">
            {tokens.map((tok) => (
              <li key={tok.id} className="flex items-center justify-between p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium">
                    <KeyRound className="h-4 w-4 text-text-secondary" />
                    {tok.name}
                  </div>
                  <div className="text-xs text-text-secondary">
                    voysi_pat_…{tok.last4} · {t('tokens.scopeCount', { count: tok.scopes.length })} ·{' '}
                    {t('tokens.createdOn', { date: new Date(tok.created_at * 1000).toLocaleDateString() })}
                    {tok.expires_at &&
                      ` · ${t('tokens.expiresOn', { date: new Date(tok.expires_at * 1000).toLocaleDateString() })}`}
                    {tok.last_used_at &&
                      ` · ${t('tokens.lastUsedOn', { date: new Date(tok.last_used_at * 1000).toLocaleString() })}`}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirm(t('tokens.revokeConfirm', { name: tok.name }))) revoke.mutate(tok.id)
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-error/40 px-3 py-1.5 text-xs text-error hover:bg-error/10"
                >
                  <Trash2 className="h-3.5 w-3.5" /> {t('tokens.revoke')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
